<script setup>
// One operator ticket (TOPS-213): title, status, assignee, participants,
// messages and notes. Shown in the chat's panel and in the «Тикеты» tab;
// the parent does the saving.
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import { useMessageFormatter } from 'shared/composables/useMessageFormatter';
import { useUISettings } from 'dashboard/composables/useUISettings';
import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  ticket: { type: Object, required: true },
  isSaving: { type: Boolean, default: false },
  // Taking a message out goes through the chat, so only from inside it.
  canRemoveMessages: { type: Boolean, default: true },
});

const emit = defineEmits([
  'update',
  'addNote',
  'deleteNote',
  'removeMessage',
  'goToMessage',
  'addParticipant',
  'removeParticipant',
]);

const { t } = useI18n();
// A preview: the words without markdown's stars and brackets.
const { getPlainText } = useMessageFormatter();
const agents = useMapGetter('agents/getAgents');
const currentUserId = useMapGetter('getCurrentUserID');
const noteText = ref('');

const addableAgents = computed(() => {
  const taken = new Set(props.ticket.participants.map(p => p.id));
  return agents.value.filter(agent => !taken.has(agent.id));
});

// Two ways to read a ticket, chosen by each operator and remembered:
// messages and notes in their own sections, or one feed in time order, the
// way a messenger reads (messages by when they were written in the chat).
const { uiSettings, updateUISettings } = useUISettings();
const isFeedView = computed(
  () => uiSettings.value.operator_tickets_view === 'feed'
);
const setView = view => updateUISettings({ operator_tickets_view: view });
const viewTabs = computed(() => [
  { view: 'sections', label: t('CONVERSATION.OPERATOR_TICKETS.VIEW_SECTIONS') },
  { view: 'feed', label: t('CONVERSATION.OPERATOR_TICKETS.VIEW_FEED') },
]);
const feed = computed(() =>
  [
    ...props.ticket.messages.map(message => ({
      kind: 'message',
      key: `m${message.id}`,
      at: message.created_at,
      mine: message.message_type === 'outgoing',
      message,
    })),
    ...props.ticket.notes.map(note => ({
      kind: 'note',
      key: `n${note.id}`,
      at: note.created_at,
      mine: true,
      note,
    })),
  ].sort((a, b) => a.at - b.at)
);

const formatDate = unix =>
  new Date(unix * 1000).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
  });

const toggleStatus = () =>
  emit('update', {
    status: props.ticket.status === 'open' ? 'closed' : 'open',
  });

const rename = event => {
  const title = event.target.value.trim();
  if (title && title !== props.ticket.title) emit('update', { title });
};

const addNote = () => {
  const content = noteText.value.trim();
  if (!content) return;
  emit('addNote', content);
  noteText.value = '';
};
</script>

<template>
  <div class="flex flex-col gap-2">
    <div
      class="flex flex-col gap-2 rounded-xl border border-n-weak bg-n-solid-2 p-3"
    >
      <input
        :key="ticket.id"
        :value="ticket.title"
        type="text"
        maxlength="255"
        class="!mb-0 w-full rounded-md border border-transparent bg-transparent px-1.5 py-1 text-sm font-semibold text-n-slate-12 hover:border-n-weak focus:border-n-brand"
        @change="rename"
      />
      <div class="flex items-center gap-2">
        <span
          class="flex shrink-0 items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-medium"
          :class="
            ticket.status === 'open'
              ? 'bg-n-teal-3 text-n-teal-11'
              : 'bg-n-alpha-2 text-n-slate-11'
          "
        >
          <span
            class="size-1.5 rounded-full"
            :class="ticket.status === 'open' ? 'bg-n-teal-9' : 'bg-n-slate-8'"
          />
          {{
            ticket.status === 'open'
              ? $t('CONVERSATION.OPERATOR_TICKETS.FILTER_OPEN')
              : $t('CONVERSATION.OPERATOR_TICKETS.FILTER_CLOSED')
          }}
        </span>
        <label
          class="flex min-w-0 flex-1 items-center gap-1.5 rounded-md bg-n-alpha-1 px-2"
          :title="$t('CONVERSATION.OPERATOR_TICKETS.ASSIGNEE')"
        >
          <span class="i-lucide-user-round size-3.5 shrink-0 text-n-slate-10" />
          <select
            :value="ticket.assignee_id || ''"
            class="!mb-0 min-w-0 flex-1 !border-0 !bg-transparent !px-0 py-1 text-xs"
            @change="
              emit('update', { assignee_id: $event.target.value || null })
            "
          >
            <option value="">
              {{ $t('CONVERSATION.OPERATOR_TICKETS.NO_ASSIGNEE') }}
            </option>
            <option v-for="agent in agents" :key="agent.id" :value="agent.id">
              {{ agent.available_name || agent.name }}
            </option>
          </select>
        </label>
      </div>
      <NextButton
        xs
        :faded="ticket.status === 'closed'"
        :slate="ticket.status === 'closed'"
        :icon="
          ticket.status === 'open' ? 'i-lucide-check' : 'i-lucide-rotate-ccw'
        "
        :is-loading="isSaving"
        :label="
          ticket.status === 'open'
            ? $t('CONVERSATION.OPERATOR_TICKETS.CLOSE')
            : $t('CONVERSATION.OPERATOR_TICKETS.REOPEN')
        "
        @click="toggleStatus"
      />
    </div>

    <div
      class="flex flex-col gap-1.5 rounded-xl border border-n-weak bg-n-solid-2 p-3"
    >
      <h4 class="flex items-center gap-1.5 text-xs font-medium text-n-slate-11">
        <span class="i-lucide-users size-3.5" />
        {{ $t('CONVERSATION.OPERATOR_TICKETS.PARTICIPANTS') }}
      </h4>
      <div class="flex flex-wrap gap-1">
        <span
          v-for="participant in ticket.participants"
          :key="participant.id"
          class="flex items-center gap-1 rounded-full bg-n-alpha-2 py-0.5 pl-2 pr-0.5 text-xs text-n-slate-12"
        >
          {{ participant.name }}
          <button
            type="button"
            class="flex size-4 items-center justify-center rounded-full text-n-slate-10 hover:bg-n-alpha-3 hover:text-n-slate-12"
            :title="$t('CONVERSATION.OPERATOR_TICKETS.REMOVE_PARTICIPANT')"
            @click="emit('removeParticipant', participant.id)"
          >
            <span class="i-lucide-x size-3" />
          </button>
        </span>
        <span
          v-if="!ticket.participants.length"
          class="text-xs text-n-slate-10"
        >
          {{ $t('CONVERSATION.OPERATOR_TICKETS.NO_PARTICIPANTS') }}
        </span>
      </div>
      <select
        :key="ticket.participants.length"
        value=""
        class="!mb-0 rounded-md !border-n-weak !bg-transparent py-1 text-xs"
        @change="emit('addParticipant', Number($event.target.value))"
      >
        <option value="" disabled>
          {{ $t('CONVERSATION.OPERATOR_TICKETS.ADD_PARTICIPANT') }}
        </option>
        <option
          v-for="agent in addableAgents"
          :key="agent.id"
          :value="agent.id"
        >
          {{ agent.available_name || agent.name }}
        </option>
      </select>
      <p class="text-[11px] leading-tight text-n-slate-10">
        {{ $t('CONVERSATION.OPERATOR_TICKETS.PARTICIPANTS_HINT') }}
      </p>
    </div>
    <div class="grid grid-cols-2 gap-1 rounded-lg bg-n-alpha-1 p-1">
      <button
        v-for="tab in viewTabs"
        :key="tab.view"
        type="button"
        class="rounded-md px-2 py-1 text-xs font-medium transition-colors"
        :class="
          (tab.view === 'feed') === isFeedView
            ? 'bg-n-solid-3 text-n-slate-12 shadow-sm'
            : 'text-n-slate-11 hover:text-n-slate-12'
        "
        @click="setView(tab.view)"
      >
        {{ tab.label }}
      </button>
    </div>

    <template v-if="!isFeedView">
      <h4
        class="flex items-center gap-1.5 pt-2 text-xs font-medium text-n-slate-11"
      >
        <span class="i-lucide-message-square size-3.5" />
        {{ $t('CONVERSATION.OPERATOR_TICKETS.MESSAGES') }}
        <span class="rounded-full bg-n-alpha-2 px-1.5 text-[11px] tabular-nums">
          {{ ticket.messages.length }}
        </span>
      </h4>
      <div
        v-for="message in ticket.messages"
        :key="message.id"
        class="group flex items-start gap-1 rounded-lg border-l-2 border-n-brand bg-n-alpha-1 py-2 pl-2.5 pr-1 transition-colors hover:bg-n-alpha-2"
      >
        <button
          type="button"
          class="min-w-0 flex-1 text-left"
          :title="$t('CONVERSATION.OPERATOR_TICKETS.GO_TO_MESSAGE')"
          @click="emit('goToMessage', message)"
        >
          <span class="flex items-center gap-2 text-xs text-n-slate-11">
            <span class="truncate font-medium text-n-slate-12">
              {{ message.sender_name }}
            </span>
            <span class="shrink-0">{{ formatDate(message.created_at) }}</span>
          </span>
          <span class="mt-0.5 block text-n-slate-12 line-clamp-3">
            {{ getPlainText(message.content || '') || '📎' }}
          </span>
        </button>
        <NextButton
          v-if="canRemoveMessages"
          xs
          ghost
          slate
          icon="i-lucide-x"
          class="invisible group-hover:visible"
          :title="$t('CONVERSATION.OPERATOR_TICKETS.REMOVE_MESSAGE')"
          @click="emit('removeMessage', message)"
        />
      </div>

      <h4
        class="flex items-center gap-1.5 pt-2 text-xs font-medium text-n-slate-11"
      >
        <span class="i-lucide-notebook-pen size-3.5" />
        {{ $t('CONVERSATION.OPERATOR_TICKETS.NOTES') }}
        <span class="rounded-full bg-n-alpha-2 px-1.5 text-[11px] tabular-nums">
          {{ ticket.notes.length }}
        </span>
      </h4>
      <div
        v-for="note in ticket.notes"
        :key="note.id"
        class="group flex items-start gap-1 rounded-lg border border-n-amber-6 bg-n-amber-3 py-2 pl-2.5 pr-1"
      >
        <div class="min-w-0 flex-1">
          <span class="flex items-center gap-2 text-xs text-n-slate-11">
            <span class="truncate font-medium text-n-slate-12">
              {{ note.user_name }}
            </span>
            <span class="shrink-0">{{ formatDate(note.created_at) }}</span>
          </span>
          <span class="mt-0.5 block whitespace-pre-wrap text-n-slate-12">
            {{ note.content }}
          </span>
        </div>
        <NextButton
          v-if="note.user_id === currentUserId"
          xs
          ghost
          slate
          icon="i-lucide-trash-2"
          class="invisible group-hover:visible"
          :title="$t('CONVERSATION.OPERATOR_TICKETS.DELETE_NOTE')"
          @click="emit('deleteNote', note)"
        />
      </div>
    </template>
    <div v-else class="flex flex-col gap-2 pt-1">
      <p v-if="!feed.length" class="py-2 text-center text-xs text-n-slate-11">
        {{ $t('CONVERSATION.OPERATOR_TICKETS.FEED_EMPTY') }}
      </p>
      <div
        v-for="item in feed"
        :key="item.key"
        class="group flex items-start gap-1"
        :class="item.mine ? 'flex-row-reverse' : ''"
      >
        <button
          v-if="item.kind === 'message'"
          type="button"
          class="min-w-0 max-w-[85%] rounded-xl px-3 py-2 text-left transition-colors"
          :class="
            item.mine
              ? 'rounded-tr-sm bg-n-brand/15 hover:bg-n-brand/20'
              : 'rounded-tl-sm bg-n-alpha-2 hover:bg-n-alpha-3'
          "
          :title="$t('CONVERSATION.OPERATOR_TICKETS.GO_TO_MESSAGE')"
          @click="emit('goToMessage', item.message)"
        >
          <span class="block truncate text-xs font-medium text-n-slate-12">
            {{ item.message.sender_name }}
          </span>
          <span class="mt-0.5 block text-n-slate-12 line-clamp-6">
            {{ getPlainText(item.message.content || '') || '📎' }}
          </span>
          <span class="mt-1 block text-right text-[11px] text-n-slate-10">
            {{ formatDate(item.at) }}
          </span>
        </button>
        <div
          v-else
          class="min-w-0 max-w-[85%] rounded-xl rounded-tr-sm border border-n-amber-6 bg-n-amber-3 px-3 py-2"
        >
          <span
            class="flex items-center gap-1 truncate text-xs font-medium text-n-amber-11"
          >
            <span class="i-lucide-notebook-pen size-3 shrink-0" />
            {{ item.note.user_name }}
          </span>
          <span class="mt-0.5 block whitespace-pre-wrap text-n-slate-12">
            {{ item.note.content }}
          </span>
          <span class="mt-1 block text-right text-[11px] text-n-slate-10">
            {{ formatDate(item.at) }}
          </span>
        </div>
        <NextButton
          v-if="item.kind === 'message' && canRemoveMessages"
          xs
          ghost
          slate
          icon="i-lucide-x"
          class="invisible self-center group-hover:visible"
          :title="$t('CONVERSATION.OPERATOR_TICKETS.REMOVE_MESSAGE')"
          @click="emit('removeMessage', item.message)"
        />
        <NextButton
          v-else-if="item.kind === 'note' && item.note.user_id === currentUserId"
          xs
          ghost
          slate
          icon="i-lucide-trash-2"
          class="invisible self-center group-hover:visible"
          :title="$t('CONVERSATION.OPERATOR_TICKETS.DELETE_NOTE')"
          @click="emit('deleteNote', item.note)"
        />
      </div>
    </div>
    <div
      class="flex flex-col gap-2 rounded-xl border border-n-weak bg-n-solid-2 p-2 focus-within:border-n-brand"
    >
      <textarea
        v-model="noteText"
        rows="2"
        class="!mb-0 w-full resize-none !border-0 !bg-transparent px-1 py-0.5 text-sm"
        :placeholder="$t('CONVERSATION.OPERATOR_TICKETS.NOTE_PLACEHOLDER')"
        @keydown.enter.exact.prevent="addNote"
      />
      <div class="flex items-center justify-between gap-2">
        <span
          class="flex min-w-0 items-center gap-1 text-[11px] leading-tight text-n-slate-10"
        >
          <span class="i-lucide-eye size-3 shrink-0" />
          {{ $t('CONVERSATION.OPERATOR_TICKETS.NOTE_VISIBILITY') }}
        </span>
        <NextButton
          xs
          class="shrink-0"
          :disabled="!noteText.trim()"
          :is-loading="isSaving"
          :label="$t('CONVERSATION.OPERATOR_TICKETS.ADD_NOTE')"
          @click="addNote"
        />
      </div>
    </div>
  </div>
</template>
